/**
 * Bandeja de salida de los correos que envía Iris.
 *
 * Este archivo es la frontera entre lo que devuelve la API y lo que el panel
 * puede pintar, y existe por dos razones concretas:
 *
 * 1. **El enlace de invitación es una credencial portadora.** Quien lo tiene
 *    entra en esa cuenta. El contrato lo excluye a propósito de
 *    `GET /platform/notifications`, pero un contrato no es una barrera: basta
 *    con que alguien lo añada al serializador «para depurar» y una pantalla que
 *    pinte campos sueltos lo publicaría sin que nadie lo pida. Por eso
 *    `normalizeNotification` es una lista blanca: lo que no está en el contrato
 *    no entra en el estado del panel, así que no puede acabar en el DOM ni en
 *    una captura de pantalla pegada en un chat.
 * 2. **El estado de un correo se lee bajo presión**, con un cliente al teléfono
 *    diciendo que no le ha llegado. Un estado que el panel no reconozca no puede
 *    pintarse como «enviado» ni como «fallido»: las dos respuestas serían
 *    mentira, y una de ellas haría reenviar un correo que quizá va en camino.
 */

/**
 * Estados canónicos de un correo del sistema.
 *
 * `failed` y `unsendable` NO son el mismo estado y fundirlos manda al operador a
 * buscar donde no es:
 *
 *  - **`failed`**: se intentó enviar y no se pudo (rebote, dominio sin verificar,
 *    proveedor caído). Se reintenta solo. Se mira el DNS, el remitente, Mailgun.
 *  - **`unsendable`**: no hay con qué enviar — faltan las variables `MAILGUN_*`.
 *    No se ha intentado nada y no se reintentará hasta que alguien las cargue.
 *    Se mira la configuración del despliegue, no el correo.
 *
 * Leer «falló el envío» cuando lo que falta es una variable de entorno cuesta la
 * media hora que se pasa mirando rebotes y registros DNS.
 */
export const NOTIFICATION_STATUSES = Object.freeze([
  'queued',
  'sending',
  'sent',
  'failed',
  'unsendable',
])

/**
 * Sinónimos que se aceptan al LEER.
 *
 * El contrato congelado nombra el campo `status` pero no fija su vocabulario, y
 * una bandeja de salida sobre Mailgun tiene nombres muy habituales para lo
 * mismo. Traducirlos aquí —en un solo sitio, y sólo hacia dentro— evita que la
 * pantalla se quede en blanco el día que el backend diga `pending` en vez de
 * `queued`. Lo que NO se traduce es lo desconocido: eso cae en `unknown`.
 */
const STATUS_ALIASES = Object.freeze({
  pending: 'queued',
  scheduled: 'queued',
  enqueued: 'queued',
  retrying: 'queued',
  processing: 'sending',
  delivered: 'sent',
  error: 'failed',
  bounced: 'failed',
  not_configured: 'unsendable',
})

/** Etiqueta de la banda que explica por qué un correo no está entregado. */
const LAST_ERROR = 'platform.notificationLastError'

/**
 * `reasonLabelKey` encabeza esa banda y `hintKey` añade qué hacer. Sólo
 * `unsendable` los cambia: su motivo no es un fallo de envío sino una
 * configuración que falta, y la acción no es reintentar sino cargarla.
 */
const STATUS_DESCRIPTORS = Object.freeze({
  queued: { labelKey: 'platform.notificationStatusQueued', tone: 'info', reasonLabelKey: LAST_ERROR, hintKey: null },
  sending: { labelKey: 'platform.notificationStatusSending', tone: 'brand', reasonLabelKey: LAST_ERROR, hintKey: null },
  sent: { labelKey: 'platform.notificationStatusSent', tone: 'success', reasonLabelKey: LAST_ERROR, hintKey: null },
  failed: { labelKey: 'platform.notificationStatusFailed', tone: 'danger', reasonLabelKey: LAST_ERROR, hintKey: null },
  unsendable: {
    labelKey: 'platform.notificationStatusUnsendable',
    // Ámbar y no rojo: no es una entrega que salió mal, es una que ni siquiera
    // se ha intentado. El color separa de un vistazo las dos listas de trabajo.
    tone: 'warning',
    reasonLabelKey: 'platform.notificationBlockedReason',
    hintKey: 'platform.notificationUnsendableHint',
  },
  unknown: { labelKey: 'platform.notificationStatusUnknown', tone: 'neutral', reasonLabelKey: LAST_ERROR, hintKey: null },
})

/**
 * Plantillas conocidas. Una que no esté aquí no se traduce: se enseña su
 * identificador tal cual, que para diagnosticar vale más que un «otro».
 */
export const NOTIFICATION_TEMPLATES = Object.freeze([
  'user_invitation',
  'password_reset',
  'quota_warning',
  'channel_disconnected',
])

/**
 * Campos del contrato. **Es una lista blanca**: `normalizeNotification` copia
 * estos y descarta el resto.
 */
export const NOTIFICATION_FIELDS = Object.freeze([
  'id',
  'toEmail',
  'template',
  'status',
  'attempts',
  'lastError',
  'organizationId',
  'organizationName',
  'createdAt',
  'sentAt',
])

/**
 * Lleva cualquier estado al vocabulario canónico.
 * @param {unknown} value
 * @returns {'queued'|'sending'|'sent'|'failed'|'unknown'}
 */
export function normalizeNotificationStatus(value) {
  if (typeof value !== 'string') return 'unknown'
  const status = value.trim().toLowerCase()
  if (NOTIFICATION_STATUSES.includes(status)) return status
  return STATUS_ALIASES[status] ?? 'unknown'
}

/**
 * Estado presentable: etiqueta traducible y tono para `StatusPill`.
 * @param {unknown} value
 */
export function describeNotificationStatus(value) {
  const key = normalizeNotificationStatus(value)
  return { key, ...STATUS_DESCRIPTORS[key] }
}

/**
 * Plantilla presentable.
 * @param {unknown} template
 * @returns {{ key: string, labelKey: string, known: boolean }}
 */
export function describeNotificationTemplate(template) {
  const known = typeof template === 'string' && NOTIFICATION_TEMPLATES.includes(template)
  return {
    key: known ? template : 'unknown',
    labelKey: known
      ? `platform.notificationTemplate_${template}`
      : 'platform.notificationTemplateUnknown',
    known,
  }
}

/** Los dos estados en los que el correo no está entregado y no va en camino. */
const RESENDABLE = Object.freeze(['failed', 'unsendable'])

/**
 * ¿Tiene sentido ofrecer el reenvío?
 *
 * Un correo en cola o enviándose ya tiene un intento en vuelo y reencolarlo
 * duplicaría el mensaje; uno ya enviado no se «arregla» mandándolo otra vez. Y
 * un estado desconocido tampoco: no se toma una decisión con efectos sobre
 * información que el panel no entiende.
 *
 * **`unsendable` sí se ofrece**, aunque reenviar antes de cargar las variables
 * de Mailgun no sirva de nada. El flujo real es: falta configuración → alguien
 * la carga → hay que desatascar lo que quedó bloqueado. Esconder el botón deja
 * esa última parte sin hacer desde el panel y devuelve al operador a copiar el
 * enlace a mano, que es exactamente lo que esta pantalla vino a resolver. Un
 * clic prematuro no rompe nada —el encargo vuelve a quedar `unsendable`—, así
 * que la honestidad se pone donde no cuesta funcionalidad: en el texto, que
 * nombra las variables que faltan y avisa de que reenviar antes no cambia nada.
 *
 * @param {{ status?: string }|null|undefined} item
 */
export function isResendable(item) {
  return RESENDABLE.includes(normalizeNotificationStatus(item?.status))
}

/**
 * ¿Está bloqueado por configuración ausente? Lo que hay que hacer no es
 * reintentar, y la fila tiene que decirlo.
 * @param {{ status?: string }|null|undefined} item
 */
export function needsConfiguration(item) {
  return normalizeNotificationStatus(item?.status) === 'unsendable'
}

/**
 * Deja el elemento en la forma exacta del contrato.
 *
 * Copia por lista blanca: cualquier campo que la API añada —un `inviteUrl`, un
 * `token`, la carga útil de la plantilla— se queda fuera del objeto que devuelve
 * esta función y, por tanto, fuera del panel entero.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeNotification(raw) {
  const source = raw && typeof raw === 'object' ? raw : {}
  const attempts = Number(source.attempts)

  return {
    id: text(source.id),
    toEmail: text(source.toEmail),
    template: text(source.template),
    // El estado se guarda ya normalizado: el crudo no se conserva para que
    // ninguna vista pueda pintarlo sin pasar por el catálogo de arriba.
    status: normalizeNotificationStatus(source.status),
    attempts: Number.isFinite(attempts) && attempts >= 0 ? Math.trunc(attempts) : 0,
    // El motivo del fallo es el dato por el que se entra a esta pantalla: si no
    // viene, se deja en `null` para distinguir «sin error» de «error vacío».
    lastError: nullableText(source.lastError),
    organizationId: nullableText(source.organizationId),
    organizationName: nullableText(source.organizationName),
    createdAt: nullableText(source.createdAt),
    sentAt: nullableText(source.sentAt),
  }
}

/**
 * Campos que la API devolvió y el contrato no contempla, **por nombre**.
 *
 * Devuelve nombres y nunca valores: sirve para avisar de que el serializador se
 * ha ido del contrato sin arrastrar a ningún registro el contenido de lo que se
 * ha colado, que es justamente lo que no debe salir de la respuesta.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 * @returns {string[]}
 */
export function unexpectedNotificationFields(raw) {
  if (!raw || typeof raw !== 'object') return []
  return Object.keys(raw).filter((field) => !NOTIFICATION_FIELDS.includes(field))
}

function text(value) {
  return typeof value === 'string' ? value : ''
}

function nullableText(value) {
  return typeof value === 'string' && value !== '' ? value : null
}
